import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Link, useSearchParams } from "react-router-dom";
import axios from "../../api/axios";
import PublicPageLayout from "./PublicPageLayout";
import FormLayoutSelection from "../Generic/FormLayoutSelection";
import FormSelectSelection from "../Generic/FormSelectSelection";
import CriterionValueGroup from "../Generic/CriterionValueGroup";
import Tooltip from "@mui/material/Tooltip";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const PublicToolSelection = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const initialLoadRef = useRef(false);

    // State for selections and options
    const [panelOptions, setPanelOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [toolsInSubcategory, setToolsInSubcategory] = useState([]);

    // Criteria loading state
    const [loadingCriteria, setLoadingCriteria] = useState(false);

    // Criteria groups by weight
    const [criteriaW1, setCriteriaW1] = useState([]);
    const [criteriaW05, setCriteriaW05] = useState([]);
    const [criteriaW033, setCriteriaW033] = useState([]);
    const [selectedCVIdsW1, setSelectedCVIdsW1] = useState([]);
    const [selectedCVIdsW05, setSelectedCVIdsW05] = useState([]);
    const [selectedCVIdsW033, setSelectedCVIdsW033] = useState([]);

    // Evaluation method and result
    const [method, setMethod] = useState("MAUT");
    const [rankedTools, setRankedTools] = useState([]);
    const [evaluated, setEvaluated] = useState(false);
    const [evaluating, setEvaluating] = useState(false);

    // Load subcategory data (tools and criteria)
    const loadSubcategoryData = useCallback(async (subcategoryId) => {
        setLoadingCriteria(true);
        try {
            const [toolsRes, criteriaRes] = await Promise.all([
                axios.get(`/ToolPassports/Public/SelectOptionsBySubcategory/${subcategoryId}`),
                axios.get(`/Criterias/Public/WithValuesBySubcategory/${subcategoryId}`)
            ]);

            setToolsInSubcategory(Array.isArray(toolsRes.data) ? toolsRes.data : []);

            const allCriteria = Array.isArray(criteriaRes.data) ? criteriaRes.data : [];
            setCriteriaW1(allCriteria.filter((c) => c.weightCoefficient === 1.0));
            setCriteriaW05(allCriteria.filter((c) => c.weightCoefficient === 0.5));
            setCriteriaW033(allCriteria.filter((c) => c.weightCoefficient === 0.33));

            // Reset selected criteria values
            setSelectedCVIdsW1([]);
            setSelectedCVIdsW05([]);
            setSelectedCVIdsW033([]);
        } catch (err) {
            enqueueSnackbar(t("cannotLoadToolsOrCriteria") || "Cannot load tools or criteria", { variant: "error" });
        } finally {
            setLoadingCriteria(false);
        }
    }, [enqueueSnackbar, t]);

    // Load panels and handle URL params for pre-selection
    useEffect(() => {
        const loadPanelsAndPreselect = async () => {
            try {
                const res = await axios.get("/Panels/Public/SelectOptions");
                const panels = res.data;
                setPanelOptions(panels);

                // Check for URL params (from Compare Tools button)
                const panelParam = searchParams.get('panel');
                const categoryParam = searchParams.get('category');
                const subcategoryParam = searchParams.get('subcategory');

                if (panelParam && categoryParam && subcategoryParam && !initialLoadRef.current) {
                    initialLoadRef.current = true;

                    // Find and select panel
                    const panelOption = panels.find(p => p.value === panelParam);
                    if (panelOption) {
                        setSelectedPanel(panelOption);

                        // Load categories for this panel
                        const catRes = await axios.get(`/Categories/Public/SelectOptionsByPanel/${panelParam}`);
                        const categories = Array.isArray(catRes.data) ? catRes.data : [];
                        setCategoryOptions(categories);

                        // Find and select category
                        const categoryOption = categories.find(c => c.value === categoryParam);
                        if (categoryOption) {
                            setSelectedCategory(categoryOption);

                            // Load subcategories for this category
                            const subRes = await axios.get(`/Subcategories/Public/SelectOptionsByCategory/${categoryParam}`);
                            const subcategories = Array.isArray(subRes.data) ? subRes.data : [];
                            setSubcategoryOptions(subcategories);

                            // Find and select subcategory
                            const subcategoryOption = subcategories.find(s => s.value === subcategoryParam);
                            if (subcategoryOption) {
                                setSelectedSubcategory(subcategoryOption);
                                // Load tools and criteria for this subcategory
                                await loadSubcategoryData(subcategoryParam);
                            }
                        }
                    }
                }
            } catch (err) {
                enqueueSnackbar(t("cannotLoadPanels") || "Cannot load panels", { variant: "error" });
            }
        };
        loadPanelsAndPreselect();
    }, [enqueueSnackbar, t, searchParams, loadSubcategoryData]);

    const resetFields = () => {
        setEvaluated(false);
        setRankedTools([]);
        setCriteriaW1([]);
        setCriteriaW05([]);
        setCriteriaW033([]);
        setSelectedCVIdsW1([]);
        setSelectedCVIdsW05([]);
        setSelectedCVIdsW033([]);
    };

    // Handlers for cascading dropdowns
    const handlePanelChange = async (option) => {
        setSelectedPanel(option);
        setSelectedCategory(null);
        setCategoryOptions([]);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);
        setToolsInSubcategory([]);
        resetFields();

        if (!option) return;
        try {
            const catRes = await axios.get(`/Categories/Public/SelectOptionsByPanel/${option.value}`);
            setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
        } catch (error) {
            enqueueSnackbar(t("cannotLoadCategories") || "Cannot load categories", { variant: "error" });
        }
    };

    const handleCategoryChange = async (option) => {
        setSelectedCategory(option);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);
        setToolsInSubcategory([]);
        resetFields();

        if (!option) return;
        try {
            const subRes = await axios.get(`/Subcategories/Public/SelectOptionsByCategory/${option.value}`);
            setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);
        } catch (err) {
            enqueueSnackbar(t("cannotLoadSubcategories") || "Cannot load subcategories", { variant: "error" });
        }
    };

    const handleSubcategoryChange = async (option) => {
        setSelectedSubcategory(option);
        setToolsInSubcategory([]);
        resetFields();

        if (!option) return;
        await loadSubcategoryData(option.value);
    };

    // Handle method change
    const handleMethodChange = (e) => setMethod(e.target.value);

    // Evaluate decision-making
    const handleEvaluate = async () => {
        if (!selectedSubcategory) {
            enqueueSnackbar(t("noSubcategorySelected") || "No subcategory selected", { variant: "error" });
            return;
        }

        // Combine selected criteria value IDs
        const chosenCVIds = [
            ...selectedCVIdsW1,
            ...selectedCVIdsW05,
            ...selectedCVIdsW033,
        ];

        if (chosenCVIds.length === 0) {
            enqueueSnackbar(t("selectAtLeastOneCriteria") || "Please select at least one criteria value", { variant: "warning" });
            return;
        }

        if (toolsInSubcategory.length < 2) {
            enqueueSnackbar(t("needAtLeastTwoTools") || "Need at least 2 tools to evaluate", { variant: "warning" });
            return;
        }

        // Build a map for criteria weights
        const cvMap = {};
        const fillMap = (criteriaList) => {
            criteriaList.forEach((crit) => {
                crit.values.forEach((cv) => {
                    cvMap[cv.id] = {
                        criterionId: crit.id,
                        weight: crit.weightCoefficient,
                    };
                });
            });
        };
        fillMap(criteriaW1);
        fillMap(criteriaW05);
        fillMap(criteriaW033);

        const selectedCriteriaArray = [];
        const weightsMap = {};

        chosenCVIds.forEach((cvIdStr) => {
            const cvId = parseInt(cvIdStr, 10);
            const info = cvMap[cvId];
            if (info) {
                selectedCriteriaArray.push({
                    criteriaId: info.criterionId,
                    value: cvId,
                    weight: info.weight,
                });
                weightsMap[info.criterionId] = info.weight;
            }
        });

        const criteriaWeightsArray = Object.entries(weightsMap).map(
            ([critIdStr, w]) => ({
                criteriaId: parseInt(critIdStr, 10),
                weight: w,
            })
        );

        // Prepare tools list from toolsInSubcategory
        const finalTools = toolsInSubcategory.map((tool) => ({
            toolId: parseInt(tool.value, 10),
        }));

        const decisionRequest = {
            Method: method,
            SubcategoryId: parseInt(selectedSubcategory.value, 10),
            SelectedCriteria: selectedCriteriaArray,
            SelectedTools: finalTools,
            CriteriaWeights: criteriaWeightsArray,
        };

        setEvaluating(true);
        try {
            const response = await axios.post("/DecisionMaking/public/evaluate", decisionRequest);
            if (response.data) {
                setRankedTools(response.data);
                setEvaluated(true);
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t("evaluationError") || "Error evaluating decision-making", { variant: "error" });
        } finally {
            setEvaluating(false);
        }
    };

    // Restart selection (reset evaluation)
    const handleRestart = () => {
        setEvaluated(false);
        setRankedTools([]);
    };

    return (
        <PublicPageLayout>
            <div className="container px-3">
                <FormLayoutSelection title={t("Tool Selection")}>
                {/* Selection Row */}
                <div className="row" style={{ marginBottom: "1rem" }}>
                    <FormSelectSelection
                        label="Panel"
                        options={panelOptions}
                        value={selectedPanel}
                        onChange={handlePanelChange}
                    />
                    <FormSelectSelection
                        label="Category"
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                    />
                    <FormSelectSelection
                        label="Subcategory"
                        options={subcategoryOptions}
                        value={selectedSubcategory}
                        onChange={handleSubcategoryChange}
                    />
                </div>

                {/* Loading state for criteria */}
                {loadingCriteria ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '2rem',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid rgba(17, 45, 78, 0.1)',
                            borderTop: '4px solid #112d4e',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{
                            marginTop: '1rem',
                            color: '#112d4e',
                            fontSize: '1.1em',
                            fontWeight: '600'
                        }}>
                            {t("Loading criteria...")}
                        </p>
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </div>
                ) : (
                    <>
                        {/* Mandatory Criteria (weight = 1.00) */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 16px',
                                backgroundColor: '#dc2626',
                                borderRadius: '8px 8px 0 0',
                                marginBottom: '12px'
                            }}>
                                <PriorityHighIcon sx={{ color: '#fff', fontSize: 22 }} />
                                <div>
                                    <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                        {t("Mandatory Criteria")} (W = 1.00)
                                    </h4>
                                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                        {t("Highest priority criteria")}
                                    </span>
                                </div>
                                <span style={{
                                    marginLeft: 'auto',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.75em',
                                    fontWeight: '600'
                                }}>
                                    {selectedCVIdsW1.length} / {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                </span>
                            </div>
                            {criteriaW1.length === 0 ? (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#94a3b8',
                                    background: '#f8fafc',
                                    borderRadius: '0 0 8px 8px',
                                    border: '1px dashed #e2e8f0'
                                }}>
                                    {t("No mandatory criteria available")}
                                </div>
                            ) : (
                                criteriaW1.map((crit) => (
                                    <CriterionValueGroup
                                        key={crit.id}
                                        criterion={crit}
                                        selectedValueIds={selectedCVIdsW1}
                                        setSelectedValueIds={setSelectedCVIdsW1}
                                    />
                                ))
                            )}
                        </div>

                        {/* Criteria (weight = 0.50) */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 16px',
                                backgroundColor: '#d97706',
                                borderRadius: '8px 8px 0 0',
                                marginBottom: '12px'
                            }}>
                                <TuneIcon sx={{ color: '#fff', fontSize: 22 }} />
                                <div>
                                    <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                        {t("Criteria")} (W = 0.50)
                                    </h4>
                                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                        {t("Medium priority criteria")}
                                    </span>
                                </div>
                                <span style={{
                                    marginLeft: 'auto',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.75em',
                                    fontWeight: '600'
                                }}>
                                    {selectedCVIdsW05.length} / {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                </span>
                            </div>
                            {criteriaW05.length === 0 ? (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#94a3b8',
                                    background: '#f8fafc',
                                    borderRadius: '0 0 8px 8px',
                                    border: '1px dashed #e2e8f0'
                                }}>
                                    {t("No criteria with weight 0.50 available")}
                                </div>
                            ) : (
                                criteriaW05.map((crit) => (
                                    <CriterionValueGroup
                                        key={crit.id}
                                        criterion={crit}
                                        selectedValueIds={selectedCVIdsW05}
                                        setSelectedValueIds={setSelectedCVIdsW05}
                                    />
                                ))
                            )}
                        </div>

                        {/* Criteria (weight = 0.33) */}
                        <div style={{ marginBottom: "1.5rem" }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 16px',
                                backgroundColor: '#059669',
                                borderRadius: '8px 8px 0 0',
                                marginBottom: '12px'
                            }}>
                                <AddCircleOutlineIcon sx={{ color: '#fff', fontSize: 22 }} />
                                <div>
                                    <h4 style={{ margin: 0, color: '#fff', fontSize: '1em', fontWeight: '600' }}>
                                        {t("Criteria")} (W = 0.33)
                                    </h4>
                                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75em' }}>
                                        {t("Lower priority criteria")}
                                    </span>
                                </div>
                                <span style={{
                                    marginLeft: 'auto',
                                    background: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.75em',
                                    fontWeight: '600'
                                }}>
                                    {selectedCVIdsW033.length} / {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("selected")}
                                </span>
                            </div>
                            {criteriaW033.length === 0 ? (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#94a3b8',
                                    background: '#f8fafc',
                                    borderRadius: '0 0 8px 8px',
                                    border: '1px dashed #e2e8f0'
                                }}>
                                    {t("No criteria with weight 0.33 available")}
                                </div>
                            ) : (
                                criteriaW033.map((crit) => (
                                    <CriterionValueGroup
                                        key={crit.id}
                                        criterion={crit}
                                        selectedValueIds={selectedCVIdsW033}
                                        setSelectedValueIds={setSelectedCVIdsW033}
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}

                {/* Selection Summary & Evaluation */}
                <div style={{
                    background: '#f8fafc',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '1.5rem',
                    border: '1px solid #e2e8f0'
                }}>
                    {/* Selection Summary */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '12px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #fecaca',
                            borderLeft: '4px solid #ef4444'
                        }}>
                            <div style={{ fontSize: '0.75em', color: '#dc2626', fontWeight: '600', textTransform: 'uppercase' }}>
                                W = 1.00
                            </div>
                            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
                                {selectedCVIdsW1.length}
                            </div>
                            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
                                {t("of")} {criteriaW1.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
                            </div>
                        </div>
                        <div style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #fed7aa',
                            borderLeft: '4px solid #f59e0b'
                        }}>
                            <div style={{ fontSize: '0.75em', color: '#d97706', fontWeight: '600', textTransform: 'uppercase' }}>
                                W = 0.50
                            </div>
                            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
                                {selectedCVIdsW05.length}
                            </div>
                            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
                                {t("of")} {criteriaW05.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
                            </div>
                        </div>
                        <div style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #bbf7d0',
                            borderLeft: '4px solid #10b981'
                        }}>
                            <div style={{ fontSize: '0.75em', color: '#059669', fontWeight: '600', textTransform: 'uppercase' }}>
                                W = 0.33
                            </div>
                            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
                                {selectedCVIdsW033.length}
                            </div>
                            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
                                {t("of")} {criteriaW033.reduce((acc, c) => acc + c.values.length, 0)} {t("options")}
                            </div>
                        </div>
                        <div style={{
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #c7d2fe',
                            borderLeft: '4px solid #6366f1'
                        }}>
                            <div style={{ fontSize: '0.75em', color: '#4f46e5', fontWeight: '600', textTransform: 'uppercase' }}>
                                {t("Tools")}
                            </div>
                            <div style={{ fontSize: '1.5em', fontWeight: '700', color: '#1e293b' }}>
                                {toolsInSubcategory.length}
                            </div>
                            <div style={{ fontSize: '0.7em', color: '#64748b' }}>
                                {t("to evaluate")}
                            </div>
                        </div>
                    </div>

                    {/* Evaluation Method Selection */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginBottom: '8px'
                            }}>
                                <label style={{ fontSize: '0.9em', fontWeight: '600', color: '#374151', margin: 0 }}>
                                    {t("Evaluation Method")}
                                </label>
                                <Tooltip
                                    title={
                                        <div style={{ padding: '8px', maxWidth: '350px' }}>
                                            <strong>MAUT</strong>: Multi-Attribute Utility Theory - Simple weighted scoring, best for straightforward comparisons.<br/><br/>
                                            <strong>PROMETHEE</strong>: Outranking Method - Pairwise comparisons with net flow ranking.<br/><br/>
                                            <strong>TOPSIS</strong>: Distance-based method - Ranks by closeness to ideal solution.<br/><br/>
                                            <strong>VIKOR</strong>: Compromise ranking - Balances group utility and individual regret.<br/><br/>
                                            <strong>COPRAS</strong>: Proportional assessment - Utility degree as percentage of best.
                                        </div>
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#94a3b8', cursor: 'help' }} />
                                </Tooltip>
                            </div>
                            <select
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    fontSize: '0.95em',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    background: '#fff',
                                    color: '#1e293b',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                                value={method}
                                onChange={handleMethodChange}
                            >
                                <option value="MAUT">MAUT - Multi-Attribute Utility Theory</option>
                                <option value="PROMETHEE">PROMETHEE - Outranking Method</option>
                                <option value="TOPSIS">TOPSIS - Distance to Ideal Solution</option>
                                <option value="VIKOR">VIKOR - Compromise Ranking</option>
                                <option value="COPRAS">COPRAS - Complex Proportional Assessment</option>
                            </select>
                        </div>
                        <button
                            style={{
                                padding: '10px 28px',
                                fontSize: '0.95em',
                                fontWeight: '600',
                                backgroundColor: '#112d4e',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: evaluating ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                opacity: evaluating ? 0.7 : 1
                            }}
                            onClick={handleEvaluate}
                            disabled={evaluating}
                            onMouseEnter={(e) => {
                                if (!evaluating) {
                                    e.currentTarget.style.backgroundColor = '#1a4370';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#112d4e';
                            }}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                            {evaluating ? t("Evaluating...") : t("Evaluate Tools")}
                        </button>
                    </div>
                </div>

                {/* Evaluation Result */}
                {evaluated && (
                    <div className="row" style={{ marginBottom: "1rem" }}>
                        {/* Left column - Evaluation Results */}
                        <div className="col-12 col-md-6">
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '16px'
                            }}>
                                <EmojiEventsIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
                                <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.1em', fontWeight: '600' }}>
                                    {t("Evaluation Results")} - {method}
                                </h4>
                            </div>
                            {rankedTools.length === 0 ? (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#94a3b8',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    border: '1px dashed #e2e8f0'
                                }}>
                                    {t("No results")}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {rankedTools.map((r, idx) => {
                                        const maxScore = rankedTools[0]?.score || 1;
                                        const scorePercentage = (r.score / maxScore) * 100;
                                        const rankColors = {
                                            0: { bg: '#fef3c7', border: '#f59e0b', badge: '#b45309', icon: '🥇' },
                                            1: { bg: '#f1f5f9', border: '#94a3b8', badge: '#475569', icon: '🥈' },
                                            2: { bg: '#fed7aa', border: '#ea580c', badge: '#9a3412', icon: '🥉' }
                                        };
                                        const colors = rankColors[idx] || { bg: '#fff', border: '#e2e8f0', badge: '#64748b', icon: `#${idx + 1}` };

                                        return (
                                            <div
                                                key={r.toolId}
                                                style={{
                                                    background: colors.bg,
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: '10px',
                                                    padding: '14px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                                    <span style={{
                                                        fontSize: idx < 3 ? '1.3em' : '0.85em',
                                                        background: idx >= 3 ? colors.border : 'transparent',
                                                        color: idx >= 3 ? '#fff' : 'inherit',
                                                        width: idx >= 3 ? '24px' : 'auto',
                                                        height: idx >= 3 ? '24px' : 'auto',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: '700'
                                                    }}>
                                                        {colors.icon}
                                                    </span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontWeight: '600',
                                                            fontSize: '0.95em',
                                                            color: '#1e293b'
                                                        }}>
                                                            {r.toolName}
                                                        </div>
                                                    </div>
                                                    <span style={{
                                                        background: colors.badge,
                                                        color: '#fff',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.75em',
                                                        fontWeight: '700'
                                                    }}>
                                                        {r.score.toFixed(3)}
                                                    </span>
                                                </div>
                                                {/* Score bar */}
                                                <div style={{
                                                    height: '6px',
                                                    background: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '3px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        height: '100%',
                                                        width: `${scorePercentage}%`,
                                                        background: colors.badge,
                                                        borderRadius: '3px',
                                                        transition: 'width 0.5s ease'
                                                    }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                <button
                                    className="start-new-evaluation-btn"
                                    style={{
                                        padding: '12px 28px',
                                        fontSize: '1em',
                                        fontWeight: '600',
                                        background: '#112d4e',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        width: '100%',
                                        maxWidth: '280px'
                                    }}
                                    onClick={handleRestart}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#1a4370';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#112d4e';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <i className="bi bi-arrow-repeat"></i>
                                    {t("Start New Evaluation")}
                                </button>
                            </div>
                        </div>

                        {/* Right column - Login prompt for AI features */}
                        <div className="col-12 col-md-6">
                            <div style={{
                                background: '#f0f9ff',
                                borderRadius: '8px',
                                border: '1px solid #bae6fd',
                                padding: '24px',
                                textAlign: 'center'
                            }}>
                                <AutoAwesomeIcon sx={{ color: '#0ea5e9', fontSize: 48, marginBottom: '12px' }} />
                                <h5 style={{ color: '#0c4a6e', fontWeight: '600', marginBottom: '8px' }}>
                                    {t("Want AI-Powered Insights?")}
                                </h5>
                                <p style={{ color: '#0369a1', fontSize: '0.9em', marginBottom: '16px' }}>
                                    {t("Sign in to get personalized AI recommendations, tool comparisons, and workflow suggestions.")}
                                </p>
                                <div className="d-flex justify-content-center gap-2">
                                    <Link
                                        to="/login"
                                        className="btn"
                                        style={{
                                            backgroundColor: '#112d4e',
                                            color: '#fff',
                                            padding: '8px 24px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <LoginIcon sx={{ fontSize: 16, marginRight: '6px' }} />
                                        {t("loginTitle")}
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="btn"
                                        style={{
                                            backgroundColor: '#fff',
                                            color: '#112d4e',
                                            border: '2px solid #112d4e',
                                            padding: '8px 24px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <PersonAddIcon sx={{ fontSize: 16, marginRight: '6px' }} />
                                        {t("registerTitle")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </FormLayoutSelection>
            </div>
        </PublicPageLayout>
    );
};

export default PublicToolSelection;
